import { useEffect, useMemo, useState } from 'react'
import { apiGet, apiPost } from '../utils/api'
import { useTenant } from '../tenant/TenantContext'
import { useToasts } from './Toasts'
import { usePerms } from '../auth/PermsContext'
import { useI18n } from '../i18n'
import Confirm from './parts/Confirm'

type Schedule = {
  id: number
  endpoint_id: number
  endpoint_name?: string
  policy_id: number
  policy_name?: string
  policy_schedule?: string
  policy_backup_strategy?: string | null
  policy_full_backup_interval?: number | null
  policy_max_non_full_backups?: number | null
  repository_id?: number
  repository_name?: string
  target_id?: number
  target_name?: string
  enabled: boolean
  last_run_at?: string
  last_success_at?: string
  last_check_at?: string
  last_restore_at?: string
  last_retention_at?: string
  auto_paused?: boolean
  auto_pause_reason?: string
  auto_resume_at?: string
  kpi_state?: Record<string, any>
  next_force_full?: boolean | null
}

type Endpoint = { id: number; name: string }
type Policy = { id: number; name: string; schedule: string }
type Repository = { id: number; name: string; type: string }
type Target = { id: number; name: string; type: string }

function formatDate(value?: string) {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function badgeClass(variant: 'info' | 'warning' | 'danger' | 'success' | 'neutral' = 'info') {
  switch (variant) {
    case 'warning':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200'
    case 'danger':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200'
    case 'success':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200'
    case 'neutral':
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
    default:
      return 'inline-flex items-center px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200'
  }
}

export default function Schedules() {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id!
  const { push } = useToasts()
  const { has } = usePerms()

  const [items, setItems] = useState<Schedule[]>([])
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [policies, setPolicies] = useState<Policy[]>([])
  const [repositories, setRepositories] = useState<Repository[]>([])
  const [targetsCache, setTargetsCache] = useState<Record<number, Target[]>>({})

  const [endpointId, setEndpointId] = useState<number>(0)
  const [policyId, setPolicyId] = useState<number>(0)
  const [repositoryId, setRepositoryId] = useState<number>(0)
  const [targetId, setTargetId] = useState<number>(0)
  const [targetOptions, setTargetOptions] = useState<Target[]>([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [confirmId, setConfirmId] = useState<number | undefined>()
  const [forcingId, setForcingId] = useState<number | null>(null)

  const formatStrategy = (strategy?: string | null) => {
    if (!strategy) return null
    const key = `policy.strategy.${strategy.toLowerCase()}`
    const translated = t(key)
    return translated && translated !== key ? translated : strategy.charAt(0).toUpperCase() + strategy.slice(1)
  }

  const renderSchedulePolicyMeta = (schedule: Schedule) => {
    const strategyLabel = formatStrategy(schedule.policy_backup_strategy)
    const lines: string[] = []
    if (strategyLabel) {
      lines.push(strategyLabel)
    }
    if (typeof schedule.policy_max_non_full_backups === 'number') {
      lines.push(`${t('policy.max_non_full') || 'Max non-full'}: ${schedule.policy_max_non_full_backups}`)
    }
    if (typeof schedule.policy_full_backup_interval === 'number' && schedule.policy_full_backup_interval > 0) {
      lines.push(`${t('policy.full_interval') || 'Full interval'}: ${schedule.policy_full_backup_interval}`)
    }
    if (!lines.length) return null
    return (
      <span className="text-xs text-gray-500">
        {lines.join(' | ')}
      </span>
    )
  }

  const renderStatus = (schedule: Schedule) => {
    const chips: JSX.Element[] = []
    if (schedule.next_force_full) {
      chips.push(<span className={badgeClass('info')}>{t('schedule.status.next_force_full') || 'Next run will be a full backup'}</span>)
    }
    if (schedule.auto_paused) {
      const reason = schedule.auto_pause_reason || (t('schedule.status.paused') as string) || 'Auto paused'
      chips.push(<span className={badgeClass('warning')}>{reason}</span>)
      if (schedule.auto_resume_at) {
        const resumeText = t('schedule.status.resume', { value: formatDate(schedule.auto_resume_at) }) || formatDate(schedule.auto_resume_at)
        chips.push(<span className="text-xs text-gray-500">{resumeText}</span>)
      }
    }
    const kpiState = (schedule.kpi_state || {}) as Record<string, any>
    const badgeList = Array.isArray(kpiState.badges) ? (kpiState.badges as Array<Record<string, any>>) : []
    if (badgeList.length) {
      const recent = badgeList.slice(-2).reverse()
      recent.forEach(badge => {
        const severity = String(badge.severity || 'info').toLowerCase()
        const variant = severity === 'critical' ? 'danger' : (severity as 'info' | 'warning' | 'danger' | 'success')
        const label =
          badge.message ||
          badge.rule_label ||
          badge.rule_id ||
          (t('schedule.status.kpi_triggered') as string) ||
          'KPI triggered'
        chips.push(<span className={badgeClass(variant)}>{label}</span>)
        if (badge.triggered_at) {
          chips.push(<span className="text-xs text-gray-500">{formatDate(badge.triggered_at)}</span>)
        }
      })
    }
    const preflight = (kpiState.preflight || {}) as Record<string, any>
    if (preflight?.required) {
      chips.push(<span className={badgeClass('warning')}>{t('schedule.status.preflight_required') || 'Preflight required'}</span>)
    } else if (preflight?.last_status) {
      const status = String(preflight.last_status).toLowerCase()
      const variant = status === 'failed' ? 'danger' : 'success'
      const label = `${t('schedule.status.preflight_prefix') || 'Preflight'} ${status}`
      chips.push(<span className={badgeClass(variant)}>{label}</span>)
      if (preflight.last_run_at) {
        chips.push(<span className="text-xs text-gray-500">{formatDate(preflight.last_run_at)}</span>)
      }
    }
    if (!chips.length) {
      return <span className="text-xs text-gray-500">{t('schedule.status.ok') || 'OK'}</span>
    }
    return <div className="flex flex-col gap-1">{chips}</div>
  }

  const loadTargets = async (endpoint: number) => {
    if (!endpoint) {
      setTargetOptions([])
      setTargetId(0)
      return
    }
    if (targetsCache[endpoint]) {
      const cached = targetsCache[endpoint]
      setTargetOptions(cached)
      if (!cached.find(t => t.id === targetId)) {
        setTargetId(cached[0]?.id ?? 0)
      }
      return
    }
    try {
      const data: Target[] = await apiGet(`/endpoints/${endpoint}/targets`)
      setTargetsCache(prev => ({ ...prev, [endpoint]: data }))
      setTargetOptions(data)
      if (!data.find(t => t.id === targetId)) {
        setTargetId(data[0]?.id ?? 0)
      }
    } catch (e: any) {
      push({ text: e?.message || t('schedule.errors.targets_load_failed'), kind: 'error' })
      setTargetOptions([])
      setTargetId(0)
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      if (!tenantId) return
      const [scheds, eps, pols, repos] = await Promise.all([
        apiGet(`/tenants/${tenantId}/schedules`),
        apiGet(`/tenants/${tenantId}/endpoints`),
        apiGet(`/tenants/${tenantId}/policies`),
        apiGet(`/tenants/${tenantId}/repositories`),
      ])
      setItems(scheds)
      setEndpoints(eps)
      setPolicies(pols)
      setRepositories(repos)
      if (!endpointId && eps.length) {
        setEndpointId(eps[0].id)
      }
      if (!policyId && pols.length) {
        setPolicyId(pols[0].id)
      }
      if (!repositoryId && repos.length) {
        setRepositoryId(repos[0].id)
      }
      const effectiveEndpoint = endpointId || eps[0]?.id || 0
      if (effectiveEndpoint) {
        await loadTargets(effectiveEndpoint)
      } else {
        setTargetOptions([])
        setTargetId(0)
      }
    } catch (e: any) {
      setError(e?.message || 'Load failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  useEffect(() => {
    if (endpointId) {
      loadTargets(endpointId)
    } else {
      setTargetOptions([])
      setTargetId(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointId])

  const create = async () => {
    if (!endpointId || !policyId || !repositoryId || !targetId) {
      push({ text: t('schedule.errors.missing_fields'), kind: 'error' })
      return
    }
    try {
      await apiPost(`/tenants/${tenantId}/schedules`, {
        endpoint_id: endpointId,
        policy_id: policyId,
        repository_id: repositoryId,
        target_id: targetId,
        enabled: true,
      })
      await load()
      push({ text: t('schedule.feedback.created'), kind: 'success' })
    } catch (e: any) {
      push({ text: e?.message || t('schedule.errors.create_failed'), kind: 'error' })
    }
  }

  const triggerForceFull = async (schedule: Schedule) => {
    if (!has('schedules.write')) return
    try {
      setForcingId(schedule.id)
      await apiPost(`/schedules/${schedule.id}/force-full`, {})
      push({ text: t('schedule.feedback.force_full'), kind: 'success' })
      await load()
    } catch (e: any) {
      push({ text: e?.message || t('schedule.errors.force_full_failed'), kind: 'error' })
    } finally {
      setForcingId(null)
    }
  }

  const endpointMap = useMemo(() => Object.fromEntries(endpoints.map(e => [e.id, e.name])), [endpoints])
  const policyMap = useMemo(() => Object.fromEntries(policies.map(p => [p.id, p.name])), [policies])
  const repositoryMap = useMemo(() => Object.fromEntries(repositories.map(r => [r.id, `${r.name} (${r.type})`])), [repositories])

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{t('titles.schedules')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('help.schedules')}</p>
      <div className="grid md:grid-cols-5 gap-2 mb-4 items-end">
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('table.endpoint')}</span>
          <select className="input" value={endpointId} onChange={e => setEndpointId(Number(e.target.value))} disabled={!has('schedules.write')}>
            {endpoints.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('schedule.fields.policy')}</span>
          <select className="input" value={policyId} onChange={e => setPolicyId(Number(e.target.value))} disabled={!has('schedules.write')}>
            {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('schedule.fields.repository')}</span>
          <select className="input" value={repositoryId} onChange={e => setRepositoryId(Number(e.target.value))} disabled={!has('schedules.write')}>
            {repositories.map(r => <option key={r.id} value={r.id}>{r.name} ({r.type})</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('schedule.fields.target')}</span>
          <select className="input" value={targetId} onChange={e => setTargetId(Number(e.target.value))} disabled={!has('schedules.write')}>
            {targetOptions.map(tg => <option key={tg.id} value={tg.id}>{tg.name} ({tg.type})</option>)}
          </select>
        </label>
        <button className="btn" onClick={create} disabled={!has('schedules.write')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6Z"/></svg>
          {t('buttons.create')}
        </button>
      </div>
      {!has('schedules.write') && <p className="text-xs text-gray-500 mb-2">{t('schedule.feedback.no_permission')}</p>}
      {loading ? <p>{t('common.loading')}</p> : error ? <p className="text-red-600">{error}</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <th className="text-left">{t('table.id')}</th>
              <th className="text-left">{t('table.endpoint')}</th>
              <th className="text-left">{t('schedule.table.policy')}</th>
              <th className="text-left">{t('schedule.table.repository')}</th>
              <th className="text-left">{t('schedule.table.target')}</th>
              <th className="text-left">{t('schedule.table.last_success')}</th>
              <th className="text-left">{t('schedule.table.last_check')}</th>
              <th className="text-left">{t('schedule.table.last_restore')}</th>
              <th className="text-left">{t('schedule.table.status') || 'Status'}</th>
              <th className="text-center">{t('table.enabled')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-gray-200 dark:border-gray-700">
                <td>{s.id}</td>
                <td>{s.endpoint_name || endpointMap[s.endpoint_id] || s.endpoint_id}</td>
                <td>
                  <div className="flex flex-col">
                    <span>{s.policy_name || policyMap[s.policy_id] || s.policy_id}</span>
                    {s.policy_schedule && <span className="text-xs text-gray-500">{s.policy_schedule}</span>}
                    {renderSchedulePolicyMeta(s)}
                  </div>
                </td>
                <td>{s.repository_name || repositoryMap[s.repository_id || 0] || '-'}</td>
                <td>{s.target_name || '-'}</td>
                <td>{formatDate(s.last_success_at)}</td>
                <td>{formatDate(s.last_check_at)}</td>
                <td>{formatDate(s.last_restore_at)}</td>
                <td>{renderStatus(s)}</td>
                <td className="text-center">
                  <button className="btn-secondary" disabled={!has('schedules.write')} onClick={async () => {
                    await fetch(`/schedules/${s.id}?enabled=${(!s.enabled)}`, { method: 'PATCH', credentials: 'include' })
                    await load()
                  }}>
                    {s.enabled ? t('buttons.disable') : t('buttons.enable')}
                  </button>
                </td>
                <td className="text-right">
                  {has('schedules.write') && (
                    <div className="flex justify-end gap-2">
                      <button
                        className="btn-secondary"
                        onClick={() => triggerForceFull(s)}
                        disabled={forcingId === s.id || !!s.next_force_full}
                      >
                        {forcingId === s.id ? (t('common.loading') || 'Loading...') : (t('schedule.actions.force_full') || 'Force next full')}
                      </button>
                      <button className="btn-secondary" onClick={() => setConfirmId(s.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M7 6h10l-1 14H8L7 6Zm2-2h6l1 2H8l1-2Z"/></svg>
                        {t('buttons.delete')}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Confirm open={!!confirmId} onClose={() => setConfirmId(undefined)} onConfirm={async () => {
        if (!confirmId) return
        try {
          await fetch(`/schedules/${confirmId}`, { method: 'DELETE', credentials: 'include' })
          await load()
        } finally {
          setConfirmId(undefined)
        }
      }}>{t('schedule.feedback.confirm_delete')}</Confirm>
    </div>
  )
}


